import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Injector,
  inject,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ShButtonComponent } from '@seahorse/components';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { DIALOG_DATA, DialogFrameData, DialogRef } from './dialog.models';

/**
 * The SHIPM8 dialog frame, rendered as the content of PrimeNG's DynamicDialog
 * (which is opened with `showHeader: false`): title, close button, and the
 * body, where the caller's content component is created with `DIALOG_DATA`
 * and `DialogRef` injectable. Internal to `DialogService`.
 */
@Component({
  selector: 'sh-dialog-frame',
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [CommonModule, TranslateModule, ShButtonComponent],
  template: `
    <div class="dialog-frame">
      <div class="dialog-header">
        <span class="dialog-title">{{ frame.title | translate }}</span>
        <!-- tabindex -1: PrimeNG focuses the first focusable element on
             show, and that should be the content (Esc closes). -->
        <sh-button
          *ngIf="frame.closable"
          styleClass="dialog-close"
          icon="pi pi-times"
          severity="secondary"
          [rounded]="true"
          variant="text"
          [tabindex]="-1"
          ariaLabel="shared.terms.close"
          (click)="frame.ref._dismiss()"
        ></sh-button>
      </div>
      <div class="dialog-body">
        <ng-container
          *ngComponentOutlet="frame.component; injector: contentInjector"
        ></ng-container>
      </div>
    </div>
  `,
})
export class DialogFrameComponent {
  protected readonly frame = inject<DynamicDialogConfig<DialogFrameData>>(
    DynamicDialogConfig
  ).data as DialogFrameData;

  // Built once: a new injector per change detection would re-create the
  // content component.
  protected readonly contentInjector = Injector.create({
    providers: [
      { provide: DIALOG_DATA, useValue: this.frame.data },
      { provide: DialogRef, useValue: this.frame.ref },
    ],
    parent: inject(Injector),
  });
}
